import React, { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Drawer,
  Hidden,
  AppBar,
  Toolbar,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { Menu as MenuIcon, AccountCircleOutlined } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { AuthUserContext } from "../Session";
import CompanyLogo from "../CompanyLogo";
import SignOutButton from "../Signout";

const DRAWER_WIDTH = 220;

const useStyles = makeStyles((theme) => ({
  drawerPaper: {
    width: DRAWER_WIDTH,
    display: "flex",
    flexDirection: "column",
  },
  logoBlock: {
    display: "flex",
    justifyContent: "center",
    padding: "32px 0 24px",
  },
  navList: {
    flexGrow: 1,
    padding: "16px 8px",
  },
  navItem: {
    borderRadius: 8,
    marginBottom: 4,
  },
  accountRow: {
    padding: "12px 16px 16px",
  },
  appBarTitle: {
    flexGrow: 1,
    color: "#fff",
    marginLeft: 8,
  },
  main: {
    [theme.breakpoints.up("md")]: {
      padding: `24px 24px 24px ${DRAWER_WIDTH + 24}px`,
    },
    [theme.breakpoints.down("sm")]: {
      padding: "24px 16px",
    },
  },
}));

const NavContent = ({ navItems, onNavigate, activeRoute, email }) => {
  const classes = useStyles();

  return (
    <>
      <div className={classes.logoBlock}>
        <CompanyLogo size="sm" height="100" />
      </div>
      <Divider />
      <List className={classes.navList}>
        {navItems.map((item) => (
          <ListItem
            button
            key={item.route}
            className={classes.navItem}
            onClick={() => onNavigate(item.route)}
            selected={activeRoute === item.route}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText>{item.label}</ListItemText>
          </ListItem>
        ))}
      </List>
      <Divider />
      <ListItem className={classes.accountRow}>
        <ListItemIcon>
          <Tooltip title={email || ""}>
            <AccountCircleOutlined />
          </Tooltip>
        </ListItemIcon>
        <ListItemText>
          <SignOutButton />
        </ListItemText>
      </ListItem>
    </>
  );
};

/**
 * Shared dashboard chrome: permanent sidebar >= md, AppBar + temporary
 * drawer below. The active nav item derives from the current route, so it
 * survives refreshes and deep links.
 *
 * navItems: [{ label, icon, route }] — first item is treated as home and
 * highlighted when the path matches none of the routes (e.g. the bare
 * dashboard root).
 */
const DashboardLayout = ({ navItems, children }) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const matched = navItems.find((item) =>
    location.pathname.startsWith(item.route)
  );
  const activeRoute = matched ? matched.route : navItems[0].route;

  const onNavigate = (route) => {
    history.push(route);
    setMobileOpen(false);
  };

  return (
    <AuthUserContext.Consumer>
      {(authUser) => (
        <>
          <Hidden mdUp>
            <AppBar position="static">
              <Toolbar>
                <IconButton
                  onClick={() => setMobileOpen(true)}
                  color="inherit"
                  aria-label="open navigation"
                >
                  <MenuIcon />
                </IconButton>
                <Typography variant="h6" className={classes.appBarTitle}>
                  Smart Squirrel
                </Typography>
              </Toolbar>
            </AppBar>
            <Drawer
              open={mobileOpen}
              anchor="left"
              onClose={() => setMobileOpen(false)}
              classes={{ paper: classes.drawerPaper }}
            >
              <NavContent
                navItems={navItems}
                onNavigate={onNavigate}
                activeRoute={activeRoute}
                email={authUser && authUser.email}
              />
            </Drawer>
          </Hidden>
          <Hidden smDown>
            <Drawer
              variant="permanent"
              anchor="left"
              classes={{ paper: classes.drawerPaper }}
            >
              <NavContent
                navItems={navItems}
                onNavigate={onNavigate}
                activeRoute={activeRoute}
                email={authUser && authUser.email}
              />
            </Drawer>
          </Hidden>
          <main className={classes.main}>{children}</main>
        </>
      )}
    </AuthUserContext.Consumer>
  );
};

export default DashboardLayout;
